import { Card } from '@/common/ui/card'
import {
	ArrowDownToLine,
	ArrowUpFromLine,
	TrendingUp,
	Trophy,
} from 'lucide-react'
import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { useDateFilter } from '@/common/hooks/useDateFilter'

import useQueryGetPlayerProviderStats from '@/modules/manage-player/api/hooks/useQueryGetPlayerProviderStats'
import { providerStatsColumns } from './constants'

interface StatCardProps {
	title: string
	value: string
	subtitle?: string
	icon: React.ReactNode
	colorClass?: string
}

function StatCard({
	title,
	value,
	subtitle,
	icon,
	colorClass = 'text-primary',
}: StatCardProps) {
	return (
		<Card className="relative overflow-hidden transition-all hover:shadow-md">
			<div className="p-4 sm:p-6">
				<div className="flex items-center justify-between">
					<div className="w-full space-y-2">
						<div className={`inline-flex items-center gap-2 ${colorClass}`}>
							<div className="rounded-full bg-primary/10 p-2">{icon}</div>
							<p className="text-xs font-medium sm:text-sm">{title}</p>
						</div>
						<h3 className="text-lg font-semibold tracking-tight sm:text-2xl">
							{value}
						</h3>
						{subtitle && (
							<p className="text-xs text-muted-foreground sm:text-sm">
								{subtitle}
							</p>
						)}
					</div>
				</div>
			</div>
		</Card>
	)
}

interface PlayerProviderStatsProps {
	playerId: string
}

export default function PlayerProviderStats({
	playerId,
}: PlayerProviderStatsProps) {
	const { dateFilter } = useDateFilter()

	const providerStatsPayload = {
		playerId,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
	}

	const { data: providerStats, isLoading } =
		useQueryGetPlayerProviderStats(providerStatsPayload)

	// Calculate totals across all providers
	const totals = providerStats?.data.reduce(
		(acc, provider) => ({
			totalTransferIn: acc.totalTransferIn + provider.totalTransferIn,
			totalTransferOut: acc.totalTransferOut + provider.totalTransferOut,
			totalBetAmount: acc.totalBetAmount + provider.totalBetAmount,
			totalSettleAmount: acc.totalSettleAmount + provider.totalSettleAmount,
			totalTurnover: acc.totalTurnover + provider.totalTurnover,
			totalProfit: acc.totalProfit + provider.totalProfit,
			totalPlayCount: acc.totalPlayCount + provider.totalPlayCount,
		}),
		{
			totalTransferIn: 0,
			totalTransferOut: 0,
			totalBetAmount: 0,
			totalSettleAmount: 0,
			totalTurnover: 0,
			totalProfit: 0,
			totalPlayCount: 0,
		},
	)

	return (
		<div className="space-y-4">
			{/* Date Filter Controls */}
			<div className="flex flex-col items-end gap-4">
				<DateRangePicker />
				<DateRangeButtons />
			</div>

			{/* Summary Stats */}
			{totals && (
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					<StatCard
						title="โอนเข้ารวม"
						value={`${totals.totalTransferIn.toLocaleString('th-TH', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})} บาท`}
						icon={<ArrowDownToLine className="h-4 w-4 sm:h-5 sm:w-5" />}
						colorClass="text-green-500"
					/>
					<StatCard
						title="โอนออกรวม"
						value={`${totals.totalTransferOut.toLocaleString('th-TH', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})} บาท`}
						icon={<ArrowUpFromLine className="h-4 w-4 sm:h-5 sm:w-5" />}
						colorClass="text-red-500"
					/>
					<StatCard
						title="เทิร์นโอเวอร์รวม"
						value={`${totals.totalTurnover.toLocaleString('th-TH', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})} บาท`}
						icon={<Trophy className="h-4 w-4 sm:h-5 sm:w-5" />}
						colorClass="text-yellow-500"
					/>
					<StatCard
						title="กำไร/ขาดทุนรวม"
						value={`${totals.totalProfit >= 0 ? '+' : ''}${totals.totalProfit.toLocaleString(
							'th-TH',
							{
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							},
						)} บาท`}
						subtitle={`จำนวนเกม ${totals.totalPlayCount.toLocaleString(
							'th-TH',
						)} ครั้ง`}
						icon={<TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />}
						colorClass={
							totals.totalProfit >= 0 ? 'text-green-500' : 'text-red-500'
						}
					/>
				</div>
			)}

			{/* Provider Stats Table */}
			<DataTablePaginate
				columns={providerStatsColumns}
				data={providerStats?.data || []}
				totalItems={providerStats?.data?.length || 0}
				isLoading={isLoading}
			/>
		</div>
	)
}
